'use client';

import { useMemo } from 'react';

interface IHighlightFragmentProps {
    /** 包含高亮标签的字符串，如 "Once <em>again</em>" */
    fragment?: string;
    /** 兜底文本：当 fragment 为空时展示 */
    fallback?: string;
    /** 高亮命中部分的 className */
    highlightClassName?: string;
}

// 匹配后端可能返回的高亮标签：<em>...</em> / <span>...</span> 等
const HIGHLIGHT_TAG_REGEX = /<(em|span|b|strong|i|mark)>([\s\S]*?)<\/\1>/gi;

// 简单还原常见的 HTML 实体，避免命中部分被转义后渲染异常
function decodeHtmlEntities(input: string) {
    return input
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ');
}

export default function HighlightFragment(props: IHighlightFragmentProps) {
    const { fragment, fallback = '', highlightClassName = 'text-[#9865ff]' } = props;

    const nodes = useMemo(() => {
        const source = fragment || fallback;
        if (!source) {
            return [] as Array<{ text: string; highlight: boolean }>;
        }

        const parts: Array<{ text: string; highlight: boolean }> = [];
        HIGHLIGHT_TAG_REGEX.lastIndex = 0;
        let lastIndex = 0;

        for (let match = HIGHLIGHT_TAG_REGEX.exec(source); match !== null; match = HIGHLIGHT_TAG_REGEX.exec(source)) {
            if (match.index > lastIndex) {
                parts.push({ text: decodeHtmlEntities(source.slice(lastIndex, match.index)), highlight: false });
            }
            parts.push({ text: decodeHtmlEntities(match[2]), highlight: true });
            lastIndex = match.index + match[0].length;
        }
        if (lastIndex < source.length) {
            parts.push({ text: decodeHtmlEntities(source.slice(lastIndex)), highlight: false });
        }
        return parts;
    }, [fragment, fallback]);

    if (nodes.length === 0) {
        return null;
    }

    return (
        <>
            {nodes.map(part =>
                part.highlight ? (
                    <span key={part.text} className={highlightClassName}>
                        {part.text}
                    </span>
                ) : (
                    <span key={part.text}>{part.text}</span>
                )
            )}
        </>
    );
}
